import React, { FunctionComponent, ReactNode, useState } from "react";
import { useFocusHandling } from "../../../hooks/useFocusHandling";
import verticalStyles from "./styles/CardBasicVertical.module.css";
import horizontalStyles from "./styles/CardBasicHorizontal.module.css";
import { BasicButtonType } from "../../buttons/basic";

export type CardLayout = "vertical" | "horizontal";

export type BaseCardProps = {
  className?: string;
  title?: string;
  subtitle?: string;
  description?: string;
  image?: string;
  showFocusRing?: boolean;
  linkLabel?: string;
  linkIcon?: ReactNode;
  linkOnClick?: () => void;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;

  /** Button props */
  primaryVerticalButton?: BasicButtonType;
  secondaryVerticalButton?: BasicButtonType;
  primaryHorizontalButton?: BasicButtonType;
  secondaryHorizontalButton?: BasicButtonType;

  /** Variant props */
  size?: "sm" | "md" | "lg";
  state?: "default" | "disabled";
  type?: "elevate" | "outline" | "fill" | "ghost";

  /** Layout props */
  layout: CardLayout;
  renderLayout: (props: BaseCardRenderProps) => React.ReactElement;
};

export type BaseCardRenderProps = {
  cardId: string;
  className: string;
  size: "sm" | "md" | "lg";
  state: "default" | "disabled";
  type: "elevate" | "outline" | "fill" | "ghost";
  focus: boolean;
  handleFocus: () => void;
  handleBlur: () => void;
  isClickable: boolean;
  title?: string;
  subtitle?: string;
  description?: string;
  image?: string;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  linkLabel?: string;
  linkIcon?: ReactNode;
  linkOnClick?: () => void;
  primaryVerticalButton?: BasicButtonType;
  secondaryVerticalButton?: BasicButtonType;
  primaryHorizontalButton?: BasicButtonType;
  secondaryHorizontalButton?: BasicButtonType;
};

const BaseCard: FunctionComponent<BaseCardProps> = ({
  className = "",
  size = "sm",
  state = "default",
  type = "elevate",
  title,
  subtitle,
  description = "Your description goes here.",
  image,
  showFocusRing = false,
  iconLeft,
  iconRight,
  linkLabel,
  linkIcon,
  linkOnClick,
  primaryVerticalButton,
  secondaryVerticalButton,
  primaryHorizontalButton,
  secondaryHorizontalButton,
  layout,
  renderLayout,
}) => {
  const cardId = `quill-card-${layout}-${size}-${state}-${type}`;
  const [focus, setFocus] = useState(showFocusRing);
  const isClickable = state !== "disabled";

  // Select the appropriate styles based on layout
  const styles = layout === "vertical" ? verticalStyles : horizontalStyles;

  const { handleFocus, handleBlur } = useFocusHandling({
    isDisabled: !isClickable,
    setHighlight: setFocus,
  });

  // Prepare render props with all necessary data
  const renderProps: BaseCardRenderProps = {
    cardId,
    className,
    size,
    state,
    type,
    focus,
    handleFocus,
    handleBlur,
    isClickable,
    title,
    subtitle,
    description,
    image,
    iconLeft,
    iconRight,
    linkLabel,
    linkIcon,
    linkOnClick,
    primaryVerticalButton,
    secondaryVerticalButton,
    primaryHorizontalButton,
    secondaryHorizontalButton,
  };

  // Delegate rendering to the layout component
  return renderLayout(renderProps);
};

export default BaseCard;
